import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardMedia from '@mui/material/CardMedia'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import RaceCardActions from './RaceCardAction'

export default function RaceCard ({ race }) {
  const { t } = useTranslation('races')
  const firstEventDate = dayjs(race?.date[0])
  const year = firstEventDate.isValid() ? firstEventDate.year() : null
  // const lastEventDate = race?.date.length > 1 ? dayjs(race?.date[1]) : firstEventDate
  const earliestStartTime = useMemo(() => {
    return Object.values(race.startTimes || {}).reduce((earliest, time) => {
      const parts = time.match(/(\d+):(\d+) (\w+)/)
      let hour = parseInt(parts[1], 10)
      if (parts[3] === 'PM' && hour !== 12) hour += 12
      if (parts[3] === 'AM' && hour === 12) hour = 0

      const current = firstEventDate.clone()
        // .add(5, 'day')
        .hour(hour)
        .minute(parseInt(parts[2], 10))
      return !earliest || current.isBefore(earliest) ? current : earliest
    }, null)
  }, [race.startTimes, firstEventDate])
  const cutoffTime = useMemo(() =>
    earliestStartTime?.subtract(race.regCloseHoursBeforeStart || 12, 'hour'),
  [earliestStartTime, race.regCloseHoursBeforeStart]
  )

  const now = dayjs()
  const secondDate = race.date.length > 1 ? dayjs(race.date[1]) : null
  const lastEventDate = secondDate || firstEventDate
  const liveEndTime = lastEventDate.endOf('day')
  const happeningNow = now.isAfter(firstEventDate) && now.isBefore(liveEndTime)
  const ended = race.status === 'ended' || now.isAfter(liveEndTime)

  const states = {
    openingSoon: race.status === 'soon',
    openingLater: race.status === 'later',
    openRegClosed: race.status === 'open' && !race.registrationsClosed && cutoffTime && now.isAfter(cutoffTime),
    openRegOpen: race.status === 'open' && !race.registrationsClosed && (cutoffTime && !now.isAfter(cutoffTime)),
    closed: race.status === 'closed',
    ended: race.status === 'ended'
  }

  return (
    <Grid key={race.slug} size={{ xs: 12, sm: 6, md: 4 }}>
      <Card
        sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: ended ? 0.35 : 1
      }}
      >
        <CardActionArea component={Link} to={`/races/${race.slug}/details`}>
          {/* Image */}
          <CardMedia
            component='img'
            height='180'
            image={race.image || `https://d1hi0tbu2kom2c.cloudfront.net/img/races/i/${race.slug}.jpg`}
            alt={race.name}
          />
          {/* Content */}
        </CardActionArea>
        <CardContent sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
          {year &&
            <Typography
              aria-hidden='true'
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: { xs: '6rem', sm: '7rem', md: '8rem' },
                fontWeight: 900,
                lineHeight: 1,
                color: 'text.primary',
                opacity: 0.06,
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            >
              {year}
            </Typography>}
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between' }}>
            {/* Title and Subtitle */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant='h6' component='h3' gutterBottom>
                {race.title}
              </Typography>
              <Typography
                variant='body2' gutterBottom sx={{
                color: 'text.secondary'
              }}
              >
                {race.subtitle}
              </Typography>
            </Box>

            {/* Event Dates */}
            <Typography variant='subtitle2' color='primary' gutterBottom sx={{ fontSize: '1.225rem', fontWeight: 700 }}>
              {Array.isArray(race.date)
                ? race.date.map(date => dayjs(date).format('DD MMM')).join(', ')
                : dayjs(race.date).format('DD MMM')}
            </Typography>

            {/* Status Badge */}
            <Typography
              variant='caption'
              sx={{ display: 'block', fontWeight: 'bold', mb: 2 }}
              color={race.status === 'open' ? 'success.main' : 'warning.main'}
            >
              {states.openingSoon && <>{t('status.openingSoon')}</>}
              {states.openingLater && <>{t('status.openingLater')}</>}
              {states.openRegClosed && happeningNow && <Box sx={{ background: '#3cc480', textAlign: 'center', color: 'white', fontWeight: 'bold', height: 32, pt: 0.5, fontSize: 16 }}>{t('status.raceDay')}</Box>}
              {states.openRegClosed && !happeningNow && now.isBefore(liveEndTime) && <>{t('status.registrationsClosed')}</>}
              {states.openRegClosed && now.isAfter(liveEndTime) && <>{t('status.endedSentence')}</>}
              {states.openRegOpen && <>{t('status.openForRegistrations')}</>}
              {states.closed && <>{t('status.noLongerAvailable')}</>}
              {states.ended && <>{t('status.eventEnded')}</>}
            </Typography>

            {/* Action Buttons - Fixed at bottom */}
            <RaceCardActions {...{ race, happeningNow, ended }} />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  )
}
